"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "motion/react";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef, useEffect } from "react";
import { motion, useAnimation } from "motion/react";
import { cn } from "@/lib/utils";
export interface RadioIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface RadioIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  /** Enable continuous pulsing animation */
  continuous?: boolean;
}
const VARIANTS: Variants = {
  normal: {
    opacity: 1,
    transition: {
      duration: 0.4
    }
  },
  fadeOut: {
    opacity: 0,
    transition: {
      duration: 0.3
    }
  },
  fadeIn: (i: number) => ({
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
      delay: i * 0.1
    }
  })
};

// Optimized continuous animation using CSS for performance
const CONTINUOUS_VARIANTS: Variants = {
  pulse: (i: number) => ({
    opacity: [0.4, 1, 0.4],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: i * 0.15
    }
  })
};
const RadioIcon = forwardRef<RadioIconHandle, RadioIconProps>((t0, ref) => {
  const $ = _c(53);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      continuous: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const size = t1 === undefined ? 28 : t1;
  const continuous = t2 === undefined ? false : t2;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t3;
  let t4;
  if ($[7] !== continuous || $[8] !== controls) {
    t3 = () => {
      if (continuous) {
        controls.start("pulse");
      }
    };
    t4 = [continuous, controls];
    $[7] = continuous;
    $[8] = controls;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== continuous || $[12] !== controls) {
    t5 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: async () => {
          if (continuous) {
            controls.start("pulse");
          } else {
            await controls.start("fadeOut");
            controls.start("fadeIn");
          }
        },
        stopAnimation: () => controls.start("normal")
      };
    };
    $[11] = continuous;
    $[12] = controls;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useImperativeHandle(ref, t5);
  let t6;
  if ($[14] !== continuous || $[15] !== controls || $[16] !== onMouseEnter) {
    t6 = async e => {
      if (continuous) {
        return;
      }
      if (!isControlledRef.current) {
        await controls.start("fadeOut");
        controls.start("fadeIn");
      } else {
        onMouseEnter?.(e);
      }
    };
    $[14] = continuous;
    $[15] = controls;
    $[16] = onMouseEnter;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleMouseEnter = t6;
  let t7;
  if ($[18] !== continuous || $[19] !== controls || $[20] !== onMouseLeave) {
    t7 = e_0 => {
      if (continuous) {
        return;
      }
      if (!isControlledRef.current) {
        controls.start("normal");
      } else {
        onMouseLeave?.(e_0);
      }
    };
    $[18] = continuous;
    $[19] = controls;
    $[20] = onMouseLeave;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleMouseLeave = t7;
  const variants = continuous ? CONTINUOUS_VARIANTS : VARIANTS;
  let t8;
  if ($[22] !== className) {
    t8 = cn(className);
    $[22] = className;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 1
    };
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== controls || $[26] !== variants) {
    t10 = <motion.path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" initial={t9} variants={variants} animate={controls} custom={1} />;
    $[25] = controls;
    $[26] = variants;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 1
    };
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== controls || $[30] !== variants) {
    t12 = <motion.path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5" initial={t11} variants={variants} animate={controls} custom={0} />;
    $[29] = controls;
    $[30] = variants;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <circle cx="12" cy="12" r="2" />;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 1
    };
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== controls || $[35] !== variants) {
    t15 = <motion.path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5" initial={t14} variants={variants} animate={controls} custom={0} />;
    $[34] = controls;
    $[35] = variants;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 1
    };
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== controls || $[39] !== variants) {
    t17 = <motion.path d="M19.1 4.9C23 8.8 23 15.1 19.1 19" initial={t16} variants={variants} animate={controls} custom={1} />;
    $[38] = controls;
    $[39] = variants;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== size || $[42] !== t10 || $[43] !== t12 || $[44] !== t15 || $[45] !== t17) {
    t18 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t10}{t12}{t13}{t15}{t17}</svg>;
    $[41] = size;
    $[42] = t10;
    $[43] = t12;
    $[44] = t15;
    $[45] = t17;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== handleMouseEnter || $[48] !== handleMouseLeave || $[49] !== props || $[50] !== t18 || $[51] !== t8) {
    t19 = <div className={t8} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t18}</div>;
    $[47] = handleMouseEnter;
    $[48] = handleMouseLeave;
    $[49] = props;
    $[50] = t18;
    $[51] = t8;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  return t19;
});
RadioIcon.displayName = "RadioIcon";
export { RadioIcon };